import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { historyApi } from '../api/historyContributions';
import { ApiError } from '../lib/api';
import { toast } from '../lib/toast';
import { eventDate } from '../lib/format';
import { ContributionForm } from '../components/ContributionForm';
import { ContributionMergeDialog } from '../components/ContributionMergeDialog';
import { ContributionStatusBadge } from '../components/ContributionStatusBadge';
import { PageSkeleton } from '../components/Skeleton';
import { Button, Dialog } from '../components/ui';

/** One contribution as a reviewer sees it: editable form, files, and the decisions. */
export function ContributionReviewDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: ['contribution', id], queryFn: () => historyApi.get(id) });
  const [mergeOpen, setMergeOpen] = useState(false);
  const [noteFor, setNoteFor] = useState<'needs_info' | 'declined' | null>(null);
  const [note, setNote] = useState('');
  const [internal, setInternal] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['contribution', id] });
    void qc.invalidateQueries({ queryKey: ['contributions'] });
  };
  const review = useMutation({
    mutationFn: (status: 'needs_info' | 'declined' | 'submitted') =>
      historyApi.review(id, { status, reviewNotes: note.trim() || null, internalNotes: internal.trim() || null }),
    onSuccess: () => {
      setNoteFor(null);
      setNote('');
      refresh();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not update'),
  });

  if (isLoading) return <PageSkeleton lines={4} />;
  if (isError || !data) return <p className="text-wine-300">Contribution not found.</p>;
  const { contribution: c, files, submitter, linkedEvent, mergedEvent } = data;
  const merged = c.status === 'merged';

  return (
    <div className="space-y-5">
      <Link to="/contribute/review" className="text-xs text-cream-300/60 hover:text-gold-200">
        ← Review queue
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-cream-50">{c.payload.event?.title ?? 'Untitled contribution'}</h1>
          <p className="mt-1 text-sm text-cream-300/60">
            From{' '}
            {submitter ? (
              <Link to={`/members/${submitter.id}`} className="text-gold-200 hover:underline">
                {submitter.firstName} {submitter.lastName}
              </Link>
            ) : (
              'a member'
            )}
            {c.submittedAt ? ` · submitted ${new Date(c.submittedAt).toLocaleString()}` : ''}
            {linkedEvent && (
              <>
                {' · adds to '}
                <Link to={`/archive/${linkedEvent.id}`} className="text-gold-200 hover:underline">
                  {linkedEvent.title}
                </Link>{' '}
                ({eventDate(linkedEvent)})
              </>
            )}
          </p>
        </div>
        <ContributionStatusBadge status={c.status} />
      </div>

      {merged && mergedEvent && (
        <div className="rounded border border-gold-600/40 bg-gold-500/10 px-4 py-3 text-sm">
          <p className="text-gold-200">
            Merged into{' '}
            <Link to={`/archive/${mergedEvent.id}`} className="underline">
              {mergedEvent.title}
            </Link>{' '}
            · {eventDate(mergedEvent)}
          </p>
          {c.mergeSummary && (
            <ul className="mt-1 text-xs text-cream-300/70">
              <li>
                {c.mergeSummary.eventCreated ? 'Tasting created' : 'Existing tasting'} · {c.mergeSummary.winesCreated} wines new, {c.mergeSummary.winesUpdated} filled ·{' '}
                {c.mergeSummary.scoresCreated} scores new, {c.mergeSummary.scoresUpdated} replaced, {c.mergeSummary.scoresSkipped.length} skipped
              </li>
              <li>
                {c.mergeSummary.mediaCreated} photos/scans to the gallery · {c.mergeSummary.importBatchesCreated} spreadsheets to{' '}
                <Link to="/imports" className="underline">
                  Imports
                </Link>
                {c.mergeSummary.peopleCreated.length ? ` · people added: ${c.mergeSummary.peopleCreated.join(', ')}` : ''}
              </li>
              {c.mergeSummary.warnings.map((w, i) => (
                <li key={i} className="text-gold-200">
                  {w}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!merged && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-ink-800 bg-ink-900/40 px-4 py-3">
          <span className="mr-2 text-xs uppercase tracking-wide text-cream-300/50">Decision</span>
          {c.status !== 'declined' ? (
            <>
              <Button onClick={() => setMergeOpen(true)}>Merge into archive…</Button>
              <Button variant="ghost" onClick={() => setNoteFor('needs_info')}>
                Ask for more info…
              </Button>
              <Button variant="danger" onClick={() => setNoteFor('declined')}>
                Decline…
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => review.mutate('submitted')} loading={review.isPending}>
              Reopen
            </Button>
          )}
          {c.reviewNotes && <span className="ml-auto text-xs text-cream-300/60">Last note to contributor: “{c.reviewNotes}”</span>}
        </div>
      )}
      {c.internalNotes && !noteFor && <p className="text-xs text-cream-300/50">Internal note: {c.internalNotes}</p>}

      <ContributionForm contributionId={c.id} initial={c.payload} files={files} readOnly={merged} />

      <Dialog
        open={noteFor !== null}
        onClose={() => setNoteFor(null)}
        title={noteFor === 'needs_info' ? 'Ask the contributor for more' : 'Decline this contribution'}
        description={noteFor === 'needs_info' ? 'They get a message with your question and can edit and resubmit.' : 'They are told it was not merged, with your note if you leave one.'}
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setNoteFor(null)}>
              Cancel
            </Button>
            <Button variant={noteFor === 'declined' ? 'danger' : 'primary'} loading={review.isPending} onClick={() => noteFor && review.mutate(noteFor)}>
              {noteFor === 'needs_info' ? 'Send question' : 'Decline'}
            </Button>
          </div>
        }
      >
        <label className="block text-xs text-cream-300/70">
          <span className="mb-1 block">Note to the contributor</span>
          <textarea className="field min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
        </label>
        <label className="mt-3 block text-xs text-cream-300/70">
          <span className="mb-1 block">Internal note (reviewers only)</span>
          <input className="field" value={internal} onChange={(e) => setInternal(e.target.value)} maxLength={2000} />
        </label>
      </Dialog>

      {mergeOpen && (
        <ContributionMergeDialog
          detail={data}
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          onMerged={(eventId) => {
            setMergeOpen(false);
            refresh();
            navigate(`/archive/${eventId}`);
          }}
        />
      )}
    </div>
  );
}
